<script setup lang="ts">
import { computed, inject, onBeforeMount, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useEcxToast } from '@edgeconnex/design-system'
import { AppShell, EcxAvatar, EcxBreadcrumb, type BreadcrumbItem, type EcxAvatarSeverity, type EcxMenuItem } from '@edgeconnex/design-system'
import { handleEntra } from '@/handleEntra'
import type { ILoggedUserService } from '@/services/LoggedUserService'
import { UserPreferencesService } from '@/services/UserPreferencesService'
import { MaintenanceIntervalService } from '@/services/MaintenanceIntervalService'
import { FeatureService } from '@/services/FeatureService'
import { TelemetryService } from '@/services/TelemetryService'
import { SessionReplayService } from '@/services/SessionReplayService'
import { formatUserRoles } from '@/utils/userRoles'
import userActivityTracker from '@/composables/userActivityTracker'
import { useBuildFooterData } from '@/composables/useBuildFooterData'
import { useEnvironmentLabel } from '@/composables/useEnvironmentLabel'
import { useTheme } from '@/composables/useTheme'
import { takeImpersonationNotice } from '@/utils/impersonationNotice'
import { droppedSites, resetDroppedSites, takeDroppedSitesNotice } from '@/utils/siteScopeNotice'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faBuildingCircleArrowRight,
  faGear,
  faWrench,
  faClock,
  faCalendarDays,
  faClipboardList,
  faChartMixed,
  faGrid2,
  faHome,
  faRightFromBracket,
  faSliders,
  faFileCheck,
} from '@fortawesome/pro-light-svg-icons'
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import { isFeatureEnabled } from '@/services/featureFlags'
import ChangeTenantDialog from '@/components/system/ChangeTenantDialog.vue'
import { filterNavItems, type AppNavItem } from '@/components/system/navPrivilegeFilter'
import { reportPrivileges } from '@/components/reports/reportCatalog'
import ImpersonationHeaderChip from '@/components/system/ImpersonationHeaderChip.vue'
import UserMenuRoles from '@/components/system/UserMenuRoles.vue'
import { ActiveTenantService } from '@/services/ActiveTenantService'
import { canInAnyTenant } from '@/composables/tenantAbility'
import { useTenantPickerSync } from '@/composables/useTenantPickerSync'
import tenantAPI from '@/apiServices/tenantAPI'
import { getApiErrorMessage } from '@/utils/errorUtils'
import { allowUnload } from '@/utils/pageUnload'

// Header tenant picker: sysadmins always (with an "All" option, model value ''), others only
// when they hold roles in more than one tenant. '' marks "All tenants" in the option values and
// in persistence — it can never be a real tenant name.
const showTenantPicker = computed(() => ActiveTenantService.showPicker.value)
const { syncTenantPicker } = useTenantPickerSync()

// A sysadmin can "view as" ANY active tenant (the server exempts them from the membership
// check), so their options come from the tenant API rather than their own role assignments.
// syncTenantPicker also reconciles a stored view-as tenant that no longer exists back to "All".
onMounted(async () => {
  if (!ActiveTenantService.isSysadmin.value) return
  try {
    const response = await tenantAPI.getAll()
    syncTenantPicker(response.data)
  } catch (error) {
    console.error('Failed to load tenants for the tenant picker:', error)
    toast.add({ severity: 'error', summary: getApiErrorMessage(error, 'Failed to load tenants.'), life: 4000 })
  }
})

const tenantPickerOptions = computed(() => {
  const base = ActiveTenantService.tenantChoices.value
  // Seed the current tenant when the list doesn't (yet) carry it — a sysadmin's stored view-as
  // tenant before/without the fetch above — so the control can always display its value.
  const current = ActiveTenantService.activeTenant.value
  const tenants = current && !base.includes(current) ? [current, ...base] : base
  return [
    ...(ActiveTenantService.isSysadmin.value ? [{ label: 'All tenants', value: '' }] : []),
    ...tenants.map((t) => ({ label: t, value: t })),
  ]
})
const showChangeTenantDialog = ref(false)

function onChangeTenant(value: string) {
  switchTenant(value === '' ? null : value)
}

function switchTenant(tenant: string | null) {
  // Re-applying the already-active tenant must not trigger the reload below.
  if (tenant === ActiveTenantService.activeTenant.value) return
  // Grid filters/search are tenant-context state — clear the session copy before the switch so one
  // tenant's filters (e.g. a site search) can't silently empty every list in another.
  UserPreferencesService.clearGrids()
  ActiveTenantService.setActiveTenant(tenant)
  // Full reload so every view refetches under the new scope and CASL subjects re-evaluate.
  window.location.reload()
}
const router = useRouter()
const route = useRoute()
const toast = useEcxToast()

const breadcrumb = computed<BreadcrumbItem[]>(() => {
  const meta = route.meta.breadcrumb as
    | BreadcrumbItem[]
    | ((r: RouteLocationNormalizedLoaded) => BreadcrumbItem[])
    | undefined
  if (typeof meta === 'function') return meta(route)
  return meta ?? []
})

const { logout } = handleEntra()

const loggedUserService = inject<ILoggedUserService>('loggedUserService')!

type NavItem = AppNavItem

const allNavItems: NavItem[] = [
  { label: 'Home', icon: faHome, route: '/', group: 'top' },
  { label: 'Dashboard (Beta)', icon: faGrid2, route: '/dashboard', group: 'top', requiredPrivilege: 'read-dashboard-widgets' },
  { label: 'Work Orders', icon: faWrench, route: '/work-orders', group: 'main', requiredPrivilege: 'read-workorder' },
  { label: 'PM Calendar', icon: faCalendarDays, route: '/pm-calendar', group: 'main', requiredPrivilege: 'read-calendar' },
  {
    id: 'planning',
    label: 'Planning',
    icon: faClock,
    route: null,
    group: 'main',
    children: [
      { label: 'PM Schedules', route: '/pm-schedules', requiredPrivilege: 'read-pm-template' },
      { label: 'Tasks', route: '/tasks', requiredPrivilege: 'read-task' },
      { label: 'Moratoria', route: '/moratoria', requiredPrivilege: 'read-moratorium' },
      { label: 'Moratorium Conflicts', route: '/moratorium-conflicts', requiredPrivilege: 'read-moratorium' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: faClipboardList,
    route: null,
    group: 'main',
    children: [
      { label: 'Assets', route: '/assets', requiredPrivilege: 'read-equipment' },
      { label: 'Equipment', route: '/equipment', requiredPrivilege: 'read-equipment' },
      { label: 'Categories', route: '/categories', requiredPrivilege: 'read-equipment' },
      { label: 'Models', route: '/models', requiredPrivilege: 'read-equipment' },
    ],
  },
  { label: 'Reports', icon: faChartMixed, route: '/reports', group: 'main', requiredPrivilege: reportPrivileges },
  // Route is the section root (the router redirects it to configuration) so the sidenav's
  // startsWith() active check highlights Settings on every /admin/* page.
  { label: 'Settings', icon: faGear, route: '/admin', group: 'bottom', requiredPrivilege: 'sysadmin' },
]

// The FSR blotter sits right under Work Orders while the fsr feature key is on; read inside the computed so the flag stays reactive.
const fsrNavItem: NavItem = { label: 'FSR Blotter', icon: faFileCheck, route: '/work-orders/fsr', group: 'main', requiredPrivilege: 'read-fsr' }
const navItems = computed(() => {
  const items = isFeatureEnabled('fsr')
    ? allNavItems.flatMap((item) => (item.route === '/work-orders' ? [item, fsrNavItem] : [item]))
    : allNavItems
  return filterNavItems(items, canInAnyTenant)
})
const navGroupOrder: NonNullable<NavItem['group']>[] = ['top', 'main']
const navBottomGroupOrder: NonNullable<NavItem['group']>[] = ['bottom']

const isSidebarCollapsed = ref(false)
// Theme application/resolution (incl. 'system') is owned by useTheme; the preference source of
// truth is the loaded preferences (hydrated at bootstrap before this mounts), synced below.
const { isDarkResolved: isDarkTheme, syncFromPreferences, toggleTheme } = useTheme()
syncFromPreferences()
const { isDevMode, footerData } = useBuildFooterData({ logErrors: true })
// Label + red treatment come from Config.Config, falling back to isDevMode (PR-869).
const { environmentLabel, showEnvironmentTheme } = useEnvironmentLabel(isDevMode, 'Development')

function toggleSidebar() {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

function navigateFromSidebar(destination: string | null) {
  if (!destination || destination === route.path) return
  void router.push(destination)
}

const handleLogout = async () => {
  // Best-effort save while the session token is still valid — never let it block logout.
  try {
    await UserPreferencesService.flush()
  } catch (e) {
    console.error('Pre-logout preferences flush failed', e)
  }
  await loggedUserService.logoutAsync()
  UserPreferencesService.reset()
  // Drop the first-paint theme cache so the login screen falls back to the OS scheme
  window.localStorage.removeItem('theme')
  syncFromPreferences()
  MaintenanceIntervalService.reset()
  FeatureService.reset()
  // Logout is SPA-only, so the once-per-session notice would carry into the next user's session.
  resetDroppedSites()
  logout()
  await router.push('/login')
}

const handleTimeout = async () => {
  try {
    await UserPreferencesService.flush() // best-effort while the token may still be valid
    await loggedUserService.logoutAsync()
  } catch {
    sessionStorage.removeItem('sessionToken')
    sessionStorage.removeItem('sessionExpire')
  }
  UserPreferencesService.reset()
  window.localStorage.removeItem('theme')
  syncFromPreferences()
  MaintenanceIntervalService.reset()
  FeatureService.reset()
  sessionStorage.setItem('authError', 'Your session has timed out. Please sign in again.')
  allowUnload()
  window.location.href = '/login'
}

async function extendSession() {
  try {
    await loggedUserService.extendSessionAsync()
  } catch (error) {
    console.error('Failed to extend session:', error)
  }
}

function validateExpiration() {
  const expiresOn = sessionStorage.getItem('sessionExpire')
  if (expiresOn && Date.now() >= new Date(expiresOn).getTime()) {
    void handleTimeout()
  }
}

const userFullName = computed(() => loggedUserService.user.value?.fullName ?? '')

const avatarInitials = computed(() => {
  const parts = userFullName.value.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'U'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
})
const avatarSeverity = computed<EcxAvatarSeverity>(() =>
  loggedUserService.impersonation.value ? 'danger' : 'light',
)

const userRoles = computed(
  () =>
    loggedUserService.user.value?.userRoleAssignments
      // Legacy data carries null-role rows at runtime despite the non-nullable type.
      ?.filter((a): a is typeof a & { role: NonNullable<typeof a.role> } => Boolean(a.role))
      .map((a) => (a.tenant ? `${a.role.roleName} (${a.tenant})` : a.role.roleName)) ?? [],
)

const userMenuItems = computed<EcxMenuItem[]>(() => [
  { label: 'Preferences', faIcon: faSliders, command: () => { void router.push('/preferences') } },
  ...(showTenantPicker.value
    ? [
        { separator: true },
        { label: 'Change Tenant', faIcon: faBuildingCircleArrowRight, command: () => { showChangeTenantDialog.value = true } },
      ]
    : []),
  { separator: true },
  { label: 'Logout', faIcon: faRightFromBracket, command: () => { void handleLogout() } },
])
// The active tenant rides along with the user name. A null tenant means "All" only for
// sysadmins — for anyone else it just means no assignment, which isn't worth displaying.
const activeTenantLabel = computed(() => {
  const tenant = ActiveTenantService.activeTenant.value
  if (tenant) return tenant
  return ActiveTenantService.isSysadmin.value ? 'All' : null
})

watch(
  [loggedUserService.user, loggedUserService.impersonation],
  ([newUser, impersonation]) => {
    if (newUser?.emailAddress) {
      TelemetryService.setAuthenticatedUser(newUser.emailAddress)
    } else {
      TelemetryService.clearAuthenticatedUser()
      // No session-replay counterpart: OpenReplay can only stop() the recording, not unset the user.
    }

    // Impersonation redirects the tab rather than reloading the app, and OpenReplay resumes the
    // same session across that. Identifying the target would re-file the admin's own recorded
    // actions under a user who never signed in, so the replay stays pinned to the real actor and
    // names the target in metadata instead.
    const actor = impersonation?.isImpersonating ? impersonation.realUser : newUser
    if (!actor?.emailAddress) return

    SessionReplayService.setAuthenticatedUser({
      email: actor.emailAddress,
      fullName: actor.fullName,
      roles: formatUserRoles(actor.userRoleAssignments, { separator: ' | ', unique: true, sorted: true }),
      impersonating: impersonation?.isImpersonating
        ? (impersonation.effectiveUser?.emailAddress ?? undefined)
        : undefined,
    })
  },
  { immediate: true },
)

userActivityTracker.tracker(extendSession, handleTimeout, {
  debounceMs: 30000,
  inactivityTimeoutMs: 300000,
})

onBeforeMount(() => {
  validateExpiration()
})

onMounted(() => {
  const notice = takeImpersonationNotice()
  if (notice) toast.add(notice)
})

// Pruning to an empty filter reads as "all sites in scope", so the grid widens — say so.
watch(droppedSites, (codes) => {
  if (!codes.length) return
  const notice = takeDroppedSitesNotice()
  if (notice) toast.add(notice)
})

</script>

<template>
  <AppShell
    :is-sidebar-collapsed="isSidebarCollapsed"
    :nav-items="navItems"
    :nav-group-order="navGroupOrder"
    :nav-bottom-group-order="navBottomGroupOrder"
    :active-route="route.path"
    :is-dev-mode="showEnvironmentTheme"
    :is-dark-theme="isDarkTheme"
    :footer-data="footerData"
    :user-menu-items="userMenuItems"
    product-name="CMMS"
    product-subtitle="Operations Portal"
    :on-navigate="navigateFromSidebar"
    :on-toggle-sidebar="toggleSidebar"
    :on-toggle-theme="toggleTheme"
  >
    <template #header-left>
      <EcxBreadcrumb v-if="breadcrumb.length" :items="breadcrumb" />
    </template>
    <template #header-actions-start>
      <span
        v-if="showEnvironmentTheme"
        class="inline-flex h-8 min-w-8 shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-red-900 px-3.5 text-base font-semibold leading-none text-white"
      >
        {{ environmentLabel }}
      </span>
      <ImpersonationHeaderChip v-if="loggedUserService.impersonation.value" />
    </template>
    <template #header-user-trigger="{ triggerProps }">
      <button
        v-bind="triggerProps"
        type="button"
        class="inline-flex cursor-pointer items-center gap-4 rounded-lg py-1 pe-3 ps-2 text-content hover:border-border hover:bg-surface-hover"
        aria-label="User menu"
        title="User menu"
        data-testid="app-header-user-menu"
      >
        <span class="flex items-center gap-2">
          <EcxAvatar :label="avatarInitials" :severity="avatarSeverity" :ariaLabel="userFullName || 'User'" />
          <span class="flex max-w-[16rem] flex-col items-start leading-tight max-md:hidden">
            <span class="truncate text-sm font-semibold">
              {{ userFullName || 'User' }}<template v-if="activeTenantLabel"> ({{ activeTenantLabel }})</template>
            </span>
          </span>
        </span>
        <FontAwesomeIcon :icon="faEllipsisVertical" />
      </button>
    </template>
    <template #header-user-menu-start="{ isOpen }">
      <UserMenuRoles :roles="userRoles" :is-open="isOpen" />
    </template>
    <ChangeTenantDialog
      v-if="showChangeTenantDialog"
      v-model:visible="showChangeTenantDialog"
      :options="tenantPickerOptions"
      :current="ActiveTenantService.activeTenant.value ?? ''"
      @apply="onChangeTenant"
    />
    <router-view />
  </AppShell>
</template>
